import { Component, OnDestroy, OnInit, Input, forwardRef, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { find } from 'lodash';

import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { FolderService } from '../../../../Monitor/Core/services/folder.service';
import { TopicsService } from '../../../../Monitor/Core/services/topics.service';
import { MentionSearchListItemModel } from '../../../../Monitor/Shared/models/mention-search-list-item.model';
import { MediaSubscriptionService } from '../../../../Monitor/Core/services/media-subscription.service';
import { MediaSubscription } from '../../../../Monitor/Shared/models/media-subscription.model';

import { ThemeService } from '../../../../Social/Intelligence/services/theme.service';
import { Theme } from '../../../../Social/Intelligence/models/theme.model';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { AiAgentsListService } from '../../../../AutomationAgents/services/ai-agents-list.service';
import { CurationAgentModel } from '../../../../AutomationAgents/models/curation-agent.model';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';

import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';

import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { TagModel } from '../../../Tags/models/tag.model';
import { TagsService } from '../../../Tags/services/tags.service';
import { TagGroupModel } from '../../../Tags/models/tag-group.model';
import { TagGroupsService } from '../../../Tags/services/tag-groups.service';
import { SourceGroup } from '../../../Sources/models/source-group.model';
import { SourceGroupService } from '../../../Sources/services/source-group.service';

import { ReportSeriesModel } from '../../models/report-series.model';
import { SeriesPickerFieldModel } from '../../models/series-picker-field.model';
import { SeriesTypeEnum, mentionSeriesTypes, socialSeriesTypes } from '../../enums/series-type.enum';
import { ReportsSeriesService } from '../../services/reports-series.service';
import { socialSeriesFilterTypes } from '../../enums/series-filter-types.enum';
import { SavedMentionSearchesApi } from '@api/mentions/saved-mention-searches.api';
import { SavedMentionSearchApiModel } from '@api/mentions/model/saved-mention-search.api.model';
import { getMonitoringDateQueryInputFolderOptions, getMonitoringDateQueryInputTopicOptions } from '@Monitor/Shared/models/monitoring-date-query-input-options';
import { ReportsDashboardSearchService } from '@Common/Reports/services/reports-dashboard-search.service';
import { SearchRangeModel } from '@Monitor/Core/models/search-range.model';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { MonitorConfigService } from '@Common/MonitorShared/services/monitor-config.service';
import { AdvancedColorPickerFieldModel } from '@Common/PageBuilder/models/advanced-color-picker-field.model';

// NOTE: it's a copy from SeriesPickerItemComponent with some tweaks
// will probably have to refactor this component since it relies on the report series,
// I think the plan would be just to pass it all different sources, and not have it rely on report series so all or monitoring can use it
@Component({
    selector: 'agpr-chart-drilldown-source-picker',
    templateUrl: './chart-drilldown-source-picker.component.html',
    styleUrls: ['./chart-drilldown-source-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChartDrilldownSourcePickerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class ChartDrilldownSourcePickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
    subscriptions: Subscription[] = [];
    series: ReportSeriesModel;

    onChange: (value: ReportSeriesModel) => void;
    onTouched: () => void;

    @Input() id: string;
    @Input() showColorSwatch: boolean = false;
    @Input() isBarChart: boolean = false;
    @Input() index: number;
    @Input() defaultColors: string[] = [];
    @Input() ariaInvalid: boolean = false;
    @Input() ariaDescribedBy: string;
    @Input() ariaLabelledBy: string;
    @Input() showHiddenOption: boolean = false;
    @Input() showFilterButton: boolean = true;
    @Input() showFiltersBeside: boolean = false;
    @Input() showDateRange: boolean = false;
    @Input() showDateRangeBelow: boolean = false;
    @Output() inputFocused = new EventEmitter<void>();
    @Output() inputBlurred = new EventEmitter<void>();

    dateOptions: DropdownOptionModel[] = getMonitoringDateQueryInputTopicOptions();
    searchRange: SearchRangeModel = null;
    dateQuery: DateQueryModel;

    typeOptions: DropdownOptionModel<SeriesTypeEnum>[];
    selectedType: DropdownOptionModel<SeriesTypeEnum>;

    topics: MentionSearchListItemModel[];
    topicOptions: DropdownOptionModel[];

    folders: Folder[];
    folderOptions: DropdownOptionModel[];

    tags: TagModel[];
    tagOptions: DropdownOptionModel[];

    tagGroups: TagGroupModel[];
    tagGroupOptions: DropdownOptionModel[];

    sourceGroups: SourceGroup[];
    sourceGroupOptions: DropdownOptionModel[];

    mediaSubscriptions: MediaSubscription[];
    mediaSubscriptionOptions: DropdownOptionModel[];

    socialTopics: MediaSubscription[];
    socialTopicsOptions: DropdownOptionModel[];

    socialThemes: Theme[];
    socialThemesOptions: DropdownOptionModel[];

    aiAgents: CurationAgentModel[];
    aiAgentOptions: DropdownOptionModel[];

    selectedOption: DropdownOptionModel;
    private suggestions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);

    hasActiveFilters: boolean = false;
    showError: boolean = false;

    sourceColorField: AdvancedColorPickerFieldModel;

    globalSingleField: SeriesPickerFieldModel = new SeriesPickerFieldModel(
        null,
        'series',
        '',
        '',
        false,
        undefined,
        [
            ...mentionSeriesTypes,
            ...socialSeriesTypes
        ],
        socialSeriesFilterTypes
    );

    @Output() filtersApplied: EventEmitter<void> = new EventEmitter<void>();
    @Output() dateChanged = new EventEmitter<DateQueryModel>();

    constructor(
        private topicsService: TopicsService,
        private folderService: FolderService,
        private tagsService: TagsService,
        private tagGroupsService: TagGroupsService,
        private mediaSubscriptionService: MediaSubscriptionService,
        private sourceGroupService: SourceGroupService,
        private themeService: ThemeService,
        public agentService: AgentService,
        private aiAgentsListService: AiAgentsListService,
        private reportsSeriesService: ReportsSeriesService,
        private coreDialogService: CoreDialogService,
        public route: ActivatedRoute,
        public savedSearchesApi: SavedMentionSearchesApi,
        private mentionSearchService: ReportsDashboardSearchService,
        private monitorConfigService: MonitorConfigService,
    ) {}

    ngOnInit(): void {
        this.typeOptions = this.globalSingleField.typeOptions.map(type => {
            return {
                label: `Reports.series.type.${type}`,
                value: type
            };
        });

        this.subscriptions.push(
            this.mentionSearchService.getSourceSeries().subscribe(series => {
                // HACK: to trigger the relecting the correct source because when you change on one tab, it does not change in the other because
                // ngModel does not get retriggered because pass by reference.. and you cannot clone it because it breaks the chart redraw
                this.emitSuggestions();
                this.updateSelectedOption();
                this.refreshSelectedType();
            }),
            this.mentionSearchService.getFilters().subscribe(filters => {
                this.hasActiveFilters = !!filters.find(filter => filter.chips && filter.chips.length > 0);
            }),
            this.topicsService.getTopics().subscribe((topics) => {
                if (topics) {
                    this.topics = topics;
                    this.topicOptions = this.topics.map((topic) => {
                        return new DropdownOptionModel(topic.name, topic);
                    });
                    this.emitSuggestions();
                    this.updateSelectedOption();
                }
            }),
            this.folderService.all$.subscribe((folders) => {
                this.folders = folders || [];
                this.folderOptions = this.folders.map((folder) => {
                    return new DropdownOptionModel(folder.name, folder);
                });
                this.emitSuggestions();
                this.updateSelectedOption();
            }),
            this.tagsService.all$.subscribe((tagList) => {
                if (!tagList) {
                    return;
                }
                this.tags = tagList;
                this.tagOptions = this.tags.map(tag => {
                    return new DropdownOptionModel(tag.name, tag);
                });
                this.emitSuggestions();
                this.updateSelectedOption();
            }),
            this.tagGroupsService.getTagGroupsList().subscribe((tagGroupsList) => {
                if (!tagGroupsList) {
                    return;
                }

                this.tagGroups = tagGroupsList;
                this.tagGroupOptions = this.tagGroups.map(tagGroup => {
                    return new DropdownOptionModel(tagGroup.name, tagGroup);
                });
                this.emitSuggestions();
            }),
            this.mediaSubscriptionService.all$.subscribe(mediaSubscriptions => {
                if (!mediaSubscriptions) {
                    return;
                }

                this.mediaSubscriptions = mediaSubscriptions;
                this.mediaSubscriptionOptions = this.mediaSubscriptions.map(x => new DropdownOptionModel(x.name, x));
                this.emitSuggestions();
            }),
            this.agentService.all$.subscribe(agents => {
                if (!agents) {
                    return;
                }

                this.socialTopics = agents;
                this.socialTopicsOptions = this.socialTopics.map(x => new DropdownOptionModel(x.name, x));
                this.updateSelectedOption();
            }),
            this.sourceGroupService.all$.subscribe((sourceGroupsList) => {
                if (!sourceGroupsList) {
                    return;
                }

                this.sourceGroups = sourceGroupsList;
                this.sourceGroupOptions = this.sourceGroups.map(sourceGroup => {
                    return new DropdownOptionModel(sourceGroup.name, sourceGroup);
                });
                this.emitSuggestions();
            }),
            this.themeService.all$.subscribe((socialThemesList) => {
                if (!socialThemesList) {
                    return;
                }

                this.socialThemes = socialThemesList;
                this.socialThemesOptions = this.socialThemes.map(theme => {
                    return new DropdownOptionModel(theme.name, theme);
                });
                this.emitSuggestions();
                this.updateSelectedOption();
            }),
            this.aiAgentsListService.all$.subscribe(aiAgents => {
                this.aiAgents = aiAgents;
                this.aiAgentOptions = this.aiAgents.map(aiAgent => new DropdownOptionModel(aiAgent.name, aiAgent));

                // Accounts without the agentic feature have no agents to pick from
                if (!this.aiAgents.length) {
                    this.typeOptions = this.typeOptions.filter(option => option.value !== SeriesTypeEnum.aiAgent);
                }

                this.emitSuggestions();
                this.updateSelectedOption();
            }),
            this.reportsSeriesService.showSourceValidateError$.subscribe(x => {
                if (x) {
                    this.validateSeries();
                }
            }),
            this.mentionSearchService.getDateQuery().subscribe(dateQuery => {
                this.dateQuery = dateQuery;
            }),
            this.monitorConfigService.getSearchRange().subscribe(
                searchRange => {
                    this.searchRange = searchRange;
                }
            ),
        );

        this.sourceColorField = AdvancedColorPickerFieldModel.create();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    onInputFocus(){
        this.inputFocused.emit();
    }

    onInputBlur(){
        this.inputBlurred.emit();
    }

    writeValue(series: ReportSeriesModel): void {
        if (!series) {
            return;
        }
        this.series = series;

        this.refreshSelectedType();

        if (this.socialTopics?.length && this.series?.mentions?.socialTopicId) {
            this.series.styles.socialTopicThemeIds = find(this.socialTopics, {id: this.series?.mentions?.socialTopicId})?.themeIds;
        }
        if (this.socialThemes?.length && this.series?.mentions?.themeId) {
            this.series.styles.socialTopicIds = find(this.socialThemes, {id: this.series?.mentions?.themeId})?.subscriptionIds;
        }

        this.emitSuggestions();
        this.updateSelectedOption();
        if (this.onChange) {
            this.onChange(this.series);
        }
    }

    refreshSelectedType(): void {
        if (this.series?.type && this.typeOptions?.length > 0 ) {
            const selectedTypeIndex = this.typeOptions.findIndex(option => this.series.type === option.value);
            if (selectedTypeIndex > -1) {
                this.selectedType = this.typeOptions[selectedTypeIndex];
            }
        }
    }

    registerOnChange(onChange: (value: ReportSeriesModel) => void): void {
        this.onChange = onChange;
    }
    registerOnTouched(onTouched: () => void): void {
        this.onTouched = onTouched;
    }

    getSuggestions$(): Observable<DropdownOptionModel[]> {
        return this.suggestions$;
    }


    /**
     * Updates the selected options for the series
     */
    updateSelectedOption(): void {
        if (!this.series?.mentions) {
            return;
        }

        this.selectedOption = null;

        if (this.series.mentions.folderId && this.folderOptions) {
            this.selectedOption = this.folderOptions.find((folderOption) => {
                return (folderOption.value as Folder).id === this.series.mentions.folderId;
            });
        } else if (this.series.mentions.topicId && this.topicOptions) {
            this.selectedOption = this.topicOptions.find((topicOption) => {
                return (topicOption.value as Folder).id === this.series.mentions.topicId;
            });
        } else if (this.series.mentions.tagId && this.tagOptions) {
            this.selectedOption = this.tagOptions.find((tagOption) => {
                return (tagOption.value as Folder).id === this.series.mentions.tagId;
            });
        } else if (this.series.mentions.tagGroupId && this.tagGroupOptions) {
            this.selectedOption = this.tagGroupOptions.find((tagGroupOption) => {
                return (tagGroupOption.value as Folder).id === this.series.mentions.tagGroupId;
            });
        } else if (this.series.mentions.mediaSubscriptionId && this.mediaSubscriptionOptions) {
            this.selectedOption = this.mediaSubscriptionOptions.find((mediaSubscriptionOption) => {
                return (mediaSubscriptionOption.value as Folder).id === this.series.mentions.mediaSubscriptionId;
            });
        } else if (this.series.mentions.sourceGroupId && this.sourceGroupOptions) {
            this.selectedOption = this.sourceGroupOptions.find((sourceGroupOption) => {
                return (sourceGroupOption.value as Folder).id === this.series.mentions.sourceGroupId;
            });
        } else if (this.series.mentions.socialTopicId && this.socialTopicsOptions) {
            this.selectedOption = this.socialTopicsOptions.find((socialTopicOption) => {
                return (socialTopicOption.value as Folder).id === this.series.mentions.socialTopicId;
            });
        } else if (this.series.mentions.aiAgentId && this.aiAgentOptions) {
            this.selectedOption = this.aiAgentOptions.find((aiAgentOption) => {
                return (aiAgentOption.value as CurationAgentModel).id === this.series.mentions.aiAgentId;
            });
        } else if (this.series.mentions.themeId && this.socialThemesOptions) {
            this.selectedOption = this.socialThemesOptions.find((socialThemeOption) => {
                return (socialThemeOption.value as Folder).id === this.series.mentions.themeId;
            });
        }
    }

    /**
     * Selects a type of series
     */
    selectType(type: SeriesTypeEnum): void {
        if (this.series?.label) {
            if (this.series.type === SeriesTypeEnum.topic && this.series.mentions.topicId) {
                const oldTopic = this.topics.find((topicItem) => topicItem.id === this.series.mentions.topicId);
                if (this.series.label === oldTopic.name) {
                    this.series.label = '';
                }
            } else if (this.series.type === SeriesTypeEnum.folder && this.series.mentions.folderId) {
                const oldFolder = this.folders.find((folderItem) => folderItem.id === this.series.mentions.folderId);
                if (this.series.label === oldFolder.name) {
                    this.series.label = '';
                }
                if (DateQueryModel.equals(this.dateQuery, DateQueryModel.getAllQuery())) {
                    this.dateQuery = DateQueryModel.getLast24HoursQuery();
                }
            } else if (this.series.type === SeriesTypeEnum.tag && this.series.mentions.tagId) {
                const oldTag = this.tags.find((tagItem) => tagItem.id === this.series.mentions.tagId);
                if (this.series.label === oldTag.name) {
                    this.series.label = '';
                }
                if (DateQueryModel.equals(this.dateQuery, DateQueryModel.getAllQuery())) {
                    this.dateQuery = DateQueryModel.getLast24HoursQuery();
                }
            } else if (this.series.type === SeriesTypeEnum.tagGroup && this.series.mentions.tagGroupId) {
                const oldTagGroup = this.tagGroups.find((tagGroupItem) => tagGroupItem.id === this.series.mentions.tagGroupId);
                if (this.series.label === oldTagGroup.name) {
                    this.series.label = '';
                }
                if (DateQueryModel.equals(this.dateQuery, DateQueryModel.getAllQuery())) {
                    this.dateQuery = DateQueryModel.getLast24HoursQuery();
                }
            } else if (this.series.type === SeriesTypeEnum.subscription && this.series.mentions.mediaSubscriptionId) {
                const oldMediaSubscription = this.mediaSubscriptions.find(
                    (subscriptionItem) => subscriptionItem.id === this.series.mentions.mediaSubscriptionId
                );
                if (this.series.label === oldMediaSubscription.name) {
                    this.series.label = '';
                }
            } else if (this.series.type === SeriesTypeEnum.sourceGroup && this.series.mentions.sourceGroupId) {
                const oldSourceGroup = this.sourceGroups.find((sourceGroupItem) => sourceGroupItem.id === this.series.mentions.sourceGroupId);
                if (this.series.label === oldSourceGroup.name) {
                    this.series.label = '';
                }
                if (DateQueryModel.equals(this.dateQuery, DateQueryModel.getAllQuery())) {
                    this.dateQuery = DateQueryModel.getLast24HoursQuery();
                }
            } else if (this.series.type === SeriesTypeEnum.socialTopic && this.series.mentions.socialTopicId) {
                const oldSocialTopic = this.socialTopics.find((socialTopicItem) => socialTopicItem.id === this.series.mentions.socialTopicId);
                if (this.series.label === oldSocialTopic?.name) {
                    this.series.label = '';
                }
            } else if (this.series.type === SeriesTypeEnum.aiAgent && this.series.mentions.aiAgentId) {
                const oldAiAgent = this.aiAgents?.find((aiAgentItem) => aiAgentItem.id === this.series.mentions.aiAgentId);
                if (this.series.label === oldAiAgent?.name) {
                    this.series.label = '';
                }
            } else if (this.series.type === SeriesTypeEnum.theme && this.series.mentions.themeId) {
                const oldSocialTheme = this.socialThemes.find((socialThemeItem) => socialThemeItem.id === this.series.mentions.themeId);
                if (this.series.label === oldSocialTheme?.name) {
                    this.series.label = '';
                }
            }
        }
        delete this.series.mentions.topicId;
        delete this.series.mentions.mediaSubscriptionId;
        delete this.series.mentions.folderId;
        delete this.series.mentions.tagId;
        delete this.series.mentions.tagGroupId;
        delete this.series.mentions.sourceGroupId;
        delete this.series.mentions.socialTopicId;
        delete this.series.mentions.themeId;
        delete this.series.mentions.aiAgentId;

        this.series.type = type;

        this.onChange(this.series);
        this.emitSuggestions();
        this.updateSelectedOption();
    }

    public emitSuggestions(): void {
        switch (this.series && this.series.type) {
            case SeriesTypeEnum.topic:
                this.dateOptions = getMonitoringDateQueryInputTopicOptions();
                this.suggestions$.next(this.topicOptions);
                break;
            case SeriesTypeEnum.subscription:
                this.dateOptions = getMonitoringDateQueryInputTopicOptions();
                this.suggestions$.next(this.mediaSubscriptionOptions);
                break;
            case SeriesTypeEnum.tag:
                this.dateOptions = getMonitoringDateQueryInputFolderOptions();
                this.suggestions$.next(this.tagOptions);
                break;
            case SeriesTypeEnum.tagGroup:
                this.dateOptions = getMonitoringDateQueryInputFolderOptions();
                this.suggestions$.next(this.tagGroupOptions);
                break;
            case SeriesTypeEnum.folder:
                this.dateOptions = getMonitoringDateQueryInputFolderOptions();
                this.suggestions$.next(this.folderOptions);
                break;
            case SeriesTypeEnum.sourceGroup:
                this.dateOptions = getMonitoringDateQueryInputFolderOptions();
                this.suggestions$.next(this.sourceGroupOptions);
                break;
            case SeriesTypeEnum.aiAgent:
                this.dateOptions = getMonitoringDateQueryInputFolderOptions();
                this.suggestions$.next(this.aiAgentOptions);
                break;
            case SeriesTypeEnum.theme:
                this.dateOptions = getMonitoringDateQueryInputTopicOptions();
                this.suggestions$.next(this.socialThemesOptions);
                break;
            case SeriesTypeEnum.socialTopic:
                this.dateOptions = getMonitoringDateQueryInputTopicOptions();
                this.suggestions$.next(this.socialTopicsOptions);
                break;
        }
    }

    /**
     * Selects the series data source
     */
    async selectItem(item: DropdownOptionModel) {
        const oldName = this.series.label;
        let newName = oldName;
        this.showError = false;

        // if there's a topic associated with the series, unset it -
        // will be re-assigned in the case of selecting a topic later
        this.series.mentions.topic = null;

        switch (this.series.type) {
            case SeriesTypeEnum.topic: {
                const oldValue = this.series.mentions.topicId;
                let useDefaultLabel = !oldName;

                if (oldValue && oldName) {
                    const oldTopic = this.topics.find((topicItem) => topicItem.id === oldValue);
                    useDefaultLabel = oldName === oldTopic?.name;
                }

                if (item && item.value) {
                    const topicApi = await this.getTopicFromApi(item.value.id);
                    const topic = new MentionSearchModel(topicApi);
                    this.series.mentions.topicId = topic.id;
                    this.series.mentions.topic = topic;
                    if (useDefaultLabel) {
                        newName = topic.name;
                    }
                } else {
                    this.series.mentions.topicId = null;
                    if (useDefaultLabel) {
                        newName = null;
                    }
                }


                break;
            }
            case SeriesTypeEnum.tag: {
                const oldValue = this.series.mentions.tagId;
                let useDefaultLabel = !oldName;

                if (oldValue && oldName) {
                    const oldTag = this.tags.find((tagItem) => tagItem.id === oldValue);
                    useDefaultLabel = oldName === oldTag?.name;
                }

                if (item && item.value) {
                    const tag = item.value as Folder;
                    this.series.mentions.tagId = tag.id;
                    if (useDefaultLabel) {
                        newName = tag.name;
                    }
                } else {
                    this.series.mentions.tagId = null;
                    if (useDefaultLabel) {
                        newName = null;
                    }
                }

                break;
            }
            case SeriesTypeEnum.folder: {
                const oldValue = this.series.mentions.folderId;
                let useDefaultLabel = !oldName;

                if (oldValue && oldName) {
                    const oldFolder = this.folders.find((folderItem) => folderItem.id === oldValue);
                    useDefaultLabel = oldName === oldFolder?.name;
                }

                if (item && item.value) {
                    const folder = item.value as Folder;
                    this.series.mentions.folderId = folder.id;
                    if (useDefaultLabel) {
                        newName = folder.name;
                    }
                } else {
                    this.series.mentions.folderId = null;
                    if (useDefaultLabel) {
                        newName = null;
                    }
                }

                break;
            }
            case SeriesTypeEnum.tagGroup: {
                const oldValue = this.series.mentions.tagGroupId;
                let useDefaultLabel = !oldName;

                if (oldValue && oldName) {
                    const oldTagGroup = this.tagGroups.find((tagGroupItem) => tagGroupItem.id === oldValue);
                    useDefaultLabel = oldName === oldTagGroup?.name;
                }

                if (item && item.value) {
                    const tagGroup = item.value as Folder;
                    this.series.mentions.tagGroupId = tagGroup.id;

                    if (useDefaultLabel) {
                        newName = tagGroup.name;
                    }
                } else {
                    this.series.mentions.tagGroupId = null;
                    if (useDefaultLabel) {
                        newName = null;
                    }
                }

                break;
            }
            case SeriesTypeEnum.subscription: {
                const oldValue = this.series.mentions.mediaSubscriptionId;
                let useDefaultLabel = !oldName;

                if (oldValue && oldName) {
                    const oldMediaSubscription = this.mediaSubscriptions.find(mediaSubscriptionItem => mediaSubscriptionItem.id === oldValue);
                    useDefaultLabel = oldName === oldMediaSubscription?.name;
                }

                if (item && item.value) {
                    const mediaSubscription = item.value as Folder;
                    this.series.mentions.mediaSubscriptionId = mediaSubscription.id;

                    if (useDefaultLabel) {
                        newName = mediaSubscription.name;
                    }
                } else {
                    this.series.mentions.mediaSubscriptionId = null;
                    if (useDefaultLabel) {
                        newName = null;
                    }
                }

                break;
            }
            case SeriesTypeEnum.sourceGroup: {
                const oldValue = this.series.mentions.sourceGroupId;
                let useDefaultLabel = !oldName;

                if (oldValue && oldName) {
                    const oldSourceGroup = this.sourceGroups.find((sourceGroupItem) => sourceGroupItem.id === oldValue);
                    useDefaultLabel = oldName === oldSourceGroup?.name;
                }

                if (item && item.value) {
                    const sourceGroup = item.value as Folder;
                    this.series.mentions.sourceGroupId = sourceGroup.id;

                    if (useDefaultLabel) {
                        newName = sourceGroup.name;
                    }
                } else {
                    this.series.mentions.sourceGroupId = null;
                    if (useDefaultLabel) {
                        newName = null;
                    }
                }

                break;
            }
            case SeriesTypeEnum.socialTopic: {
                const oldValue = this.series.mentions.socialTopicId;
                let useDefaultLabel = !oldName;

                if (oldValue && oldName) {
                    const oldSocialTopic = this.socialTopics.find(socialTopicItem => socialTopicItem.id === oldValue);
                    useDefaultLabel = oldName === oldSocialTopic?.name;
                }

                if (item && item.value) {
                    if (item.value.themeIds.length) {
                        const socialTopic = item.value as Folder;
                        this.series.mentions.socialTopicId = socialTopic.id;
                        this.series.styles.socialTopicThemeIds = item.value.clone()?.themeIds;

                        if (useDefaultLabel) {
                            newName = socialTopic.name;
                        }
                    } else {
                        this.series.mentions.socialTopicId = null;
                        newName = '';
                        const io: ConfirmDialogModel = {
                            inputs: {
                                heading: 'Reports.SocialTopicNotViewable.heading',
                                message: 'Reports.SocialTopicNotViewable.message',
                                alertType: messageSeverityEnum.success,
                                okLabel: 'Reports.SocialTopicNotViewable.okLabel',
                                id: 'SocialTopicNotViewable'
                            },
                            outputs: {
                                dialogClose: () => {}
                            }
                        };
                        this.coreDialogService.open(ConfirmDialogComponent, io);
                    }
                } else {
                    this.series.mentions.socialTopicId = null;
                    if (useDefaultLabel) {
                        newName = null;
                    }
                }

                break;
            }
            case SeriesTypeEnum.aiAgent: {
                const oldValue = this.series.mentions.aiAgentId;
                let useDefaultLabel = !oldName;

                if (oldValue && oldName) {
                    const oldAiAgent = this.aiAgents.find(aiAgentItem => aiAgentItem.id === oldValue);
                    useDefaultLabel = oldName === oldAiAgent?.name;
                }

                if (item && item.value) {
                    const aiAgent = item.value as CurationAgentModel;
                    this.series.mentions.aiAgentId = aiAgent.id;

                    if (useDefaultLabel) {
                        newName = aiAgent.name;
                    }
                } else {
                    this.series.mentions.aiAgentId = null;
                    if (useDefaultLabel) {
                        newName = null;
                    }
                }

                break;
            }
            case SeriesTypeEnum.theme: {
                const oldValue = this.series.mentions.themeId;
                let useDefaultLabel = !oldName;

                if (oldValue && oldName) {
                    const oldSocialTheme = this.socialThemes.find(socialThemeItem => socialThemeItem.id === oldValue);
                    useDefaultLabel = oldName === oldSocialTheme?.name;
                }

                if (item && item.value) {
                    const socialTheme = item.value as Theme;
                    this.series.mentions.themeId = socialTheme.id;

                    if (useDefaultLabel) {
                        newName = socialTheme.name;
                    }
                } else {
                    this.series.mentions.themeId = null;
                    if (useDefaultLabel) {
                        newName = null;
                    }
                }

                break;
            }
            default:
                console.error('Unhandled seriesTypeEnum');
        }

        this.series.label = newName;
        this.onChange(this.series);
    }

    dateRangeChanged() {
        this.dateChanged.next(this.dateQuery);
    }

    async getTopicFromApi(id: number): Promise<SavedMentionSearchApiModel> {
        return new Promise((resolve) => {
            this.savedSearchesApi.getById(id).subscribe(savedSearch => {
                resolve(savedSearch);
            });
        });
    }

    handleColorChange(colorHexString: string): void {
        this.series.color = colorHexString;
        this.onChange(this.series);
    }

    /**
     * Handles changes to the label for the data source
     */
    labelChanged(label: string): void {
        this.series.label = label;

        this.onChange(this.series);
    }

    /**
     * Handles changes to the label for the data source
     */
    filtersChanged(): void {
        this.onChange(this.series);
    }

    validateSeries(): void {
        this.showError = !this.series?.isValid();
    }

    handleFiltersApplied(): void {
        this.filtersApplied.emit();
    }
}
